import gridReadContract from '../../utils/gridReadContract.json';
import EnhancedDialog from '../EnhancedDialog';
import { ExportButton, type ExportConfiguration } from '../DataExport/ExportButton';
import { buildQueryForFieldDependentTable } from '../../utils/metadata/tables';
import { describeExportFilters, type ExportRow } from '../../utils/dataExport';
import { clientGridOrder, gridExportColumns, loadedGridRows, serverGridOrder, sortedColumnStates } from '../../utils/gridExportColumns';
import { useGlobalMetadata } from '../../utils/providers';
import { invalidateAllQueriesForTableNoRefetch, invalidateAndRefreshAllQueriesForTable } from '../../utils/metadata';
import { memo, useMemo, useCallback, useRef, useState, useEffect, type RefObject } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionCellRenderer, ActionHeader } from '../ActionCell';
import 'ag-grid-enterprise';
import './TableGrid.css';
import InternalAGGridReact from '../InternalAGGridReact';
import { EditModeToggle } from './EditModeToggle';
import { GridZoomControl } from '../ui/GridZoomControl';
import { ResetColumnsButton } from '../ui/ResetColumnsButton';
import { SaveDefaultLayoutButton } from '../ui/SaveDefaultLayoutButton';
import { saveTableListLayout } from '../../utils/tableListLayout';
import { reconcileSavedLayout, visibleColumnIds, type GridDefaultsSnapshot } from '../../utils/gridColumnDefaults';
import { ResetFiltersButton } from '../ui/ResetFiltersButton';
import { ActiveFilterChips } from '../ui/ActiveFilterChips';
import { useActiveColumnFilters } from '../ui/useActiveColumnFilters';
import { useTableZoom } from '../../utils/tableViewSettings';
import { resetColumnLayout } from '../../utils/resetColumnLayout';
import { resetGridFilters } from '../../utils/resetGridFilters';

// Types
import type { 
  TableGridProps,
  GridContext,
  ExtendedColDef,
  GridApiRef,
  ServerSideCache,
  RowSelectionConfig,
  CRMCellValueChangedEvent,
  CRMCellEditingStartedEvent,
  CurrencyFormatter,
  NumberFormatter,
  PicklistConfig,
  CRMFilterCondition,
  CRMFilterModel
} from '../../types/grid';
import type { 
  DynamicRecord
} from '../../types';
import type { QueryConditions } from '../../types/supabase';
import { getColumnConfigFromField, headerMinWidth, type ColumnConfigParams } from './utils/columnConfigGenerators';
import {
  adjustOrderByForRelationships,
  buildGridQueryConditions,
  createServerSideDataSource,
  fetchTableColumnStat,
  tableColumnStatPlan,
  type DataSourceParams,
} from './utils/gridDataSource';
import { columnMenuWithStat } from '../ui/ColumnStatMenuItem';
import type { GetMainMenuItemsParams } from 'ag-grid-community';
import { useLiveGridUpdates } from './utils/useLiveGridUpdates';
import { createCellValueChangedHandler, type CellValueChangedParams } from './utils/cellEditingHandlers';
import { applyDefaultValues } from '../../utils/metadata/fields';
import { buildTableSearchConditions } from '../../utils/tableSearchConditions';
import { workflowBreadcrumb } from '../../utils/telemetry';
import { prefetchRecordDestinationIfAbsent } from '../../utils/recordDestinationPrefetch';
import { useHoverPrefetch } from '../../hooks/useHoverPrefetch';
import { filterColumnState, useGridLayout } from '../../hooks/useGridLayout';
import { isUserColumnEvent } from '../../utils/columnState';
import { useFieldEditAccess } from '../../utils/fieldEditAccess';


// Module-level constants — a fresh `{}` default would give the datasource memo a
// new dependency on every render, and a new AG Grid datasource purges all rows.
const EMPTY_FILTERS: Record<string, any> = {};
const EMPTY_FIELDS: Record<string, any> = {};
const EMPTY_PICKLISTS: Record<string, PicklistConfig> = {};

/** Query key holding the grid's 1-based page number. */
const GRID_PAGE_PARAM = 'page';

interface WarningState {
  isOpen: boolean;
  message: string;
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * TableGrid Component - A comprehensive data grid built on AG Grid
 * 
 * This component provides a full-featured data grid with support for:
 * - Server-side data loading with filtering, sorting, and pagination
 * - Inline editing with real-time validation
 * - Relationship field lookups (both client-side and server-side)
 * - Picklist fields with dropdown editors
 * - Custom action columns with CRUD operations
 * - Controlled and uncontrolled modes for external state management
 * - Real-time collaborative editing indicators
 */
/**
 * Height the toolbar takes beyond its first line once it wraps. The grid's
 * height is a fixed viewport calc that assumes a one-line toolbar, so on a
 * narrow grid (for example with the merchant side panel open) the wrapped
 * line must come off the grid or the pagination row is pushed off screen.
 */
function useToolbarWrapExtraHeight(toolbarRef: RefObject<HTMLDivElement>) {
  const [extraHeight, setExtraHeight] = useState(0);
  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const items = Array.from(toolbar.children) as HTMLElement[];
      if (!items.length) return setExtraHeight(0);
      const bottom = (item: HTMLElement) => item.offsetTop + item.offsetHeight;
      const topItem = items.reduce((top, item) => (item.offsetTop < top.offsetTop ? item : top));
      // Items are vertically centred, so a first-line item starts above the
      // bottom of the topmost item; anything starting below it has wrapped.
      const firstLineBottom = Math.max(...items.filter((item) => item.offsetTop < bottom(topItem)).map(bottom));
      setExtraHeight(Math.max(0, Math.round(Math.max(...items.map(bottom)) - firstLineBottom)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [toolbarRef]);
  return extraHeight;
}

const TableGrid = memo<TableGridProps>(({
  routeName,
  gridConfig,
  topLevelFilters = EMPTY_FILTERS,
  extraColumnDefs,
  controlledData,
  onSelectionChanged,
  customActionCellRenderer,
  customActionHeader,
  customActionCellRendererParams,
  customActionHeaderParams,
  // Controlled mode props
  controlledMode = false,
  controlledFilterModel = null,
  controlledSortModel = null,
  controlledColumnState = null,
  controlledPageSize = null,
  onFilterChanged = null,
  onSortChanged = null,
  onColumnStateChanged,
  onPageSizeChanged = null,
  readOnly = false,
  onRecordOpen,
  hideActions = false,
  showEditModeToggle = false,
  toolbarActions,
  refreshKey,
  searchColumn = 'full_text_search',
  searchColumns,
  searchMode = 'plain_fts',
  searchPlaceholder = 'Search this table…',
  syncPageToUrl = false,
  heightOffset,
  layoutKey,
}) => {
  // Hooks
  const { data: metadata, error: metadataError, isLoading: metadataIsLoading } = useGlobalMetadata();
  const { canEditField } = useFieldEditAccess();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const toolbarWrapExtraHeight = useToolbarWrapExtraHeight(toolbarRef);
  const location = useLocation();
  const navigate = useNavigate();

  // Extract configuration
  const tableName: string = gridConfig?.table_name;
  const schemaName: string = gridConfig?.schema_name || gridConfig?.ui_data?.schema_name || 'public';
  const source = gridConfig?.ui_data?.source as 'view' | 'deepView' | 'base' | undefined;
  const showPagination: boolean = gridConfig?.ui_data?.showPagination == null ? true : gridConfig?.ui_data?.showPagination;
  const heightLayout = gridConfig?.ui_data.heightLayout || 'normal';
  // Per-table zoom, remembered across sessions per table (REG-653).
  const [tableZoom, setTableZoom] = useTableZoom(tableName);

  // Metadata extraction with type safety. These feed the column and datasource
  // memos, so they must not be rebuilt on every render.
  const fields = metadata?.fieldData?.[tableName]?.byFieldName || EMPTY_FIELDS;
  const tablePicklists: Record<string, PicklistConfig> = (metadata?.picklistData as any)?.[tableName]?.byFieldName || EMPTY_PICKLISTS;
  const clientSideTables: string[] = useMemo(
    () => Object.keys(metadata?.clientSideSelectTables || {}),
    [metadata?.clientSideSelectTables]
  );
  const columnMetadata = useMemo(() => ({
    clientSideSelectTables: metadata?.clientSideSelectTables,
    tableData: metadata?.tableData ?? undefined,
  }), [metadata?.clientSideSelectTables, metadata?.tableData]);
  const tableRelationshipLookup = metadata?.relationshipLookupData?.[tableName];
  const datasourceMetadata = useMemo(() => ({
    relationshipLookupData: {
      [tableName]: tableRelationshipLookup,
    },
  }), [tableName, tableRelationshipLookup]);

  // Refs for managing grid state
  const clientSideRowsRef = useRef<DynamicRecord[]>([]);
  const gridApiRef = useRef<GridApiRef | null>(null);
  const initialDataTelemetryRef = useRef(false);
  const serverSideCacheRef = useRef<ServerSideCache>({ rows: [], count: 0 });
  // REG-664: expanded relationship columns (`*_ref`) the grid is currently
  // showing. They are excluded from the select unless listed here, so a table
  // with an expanded relationship does not fetch the target's whole field list
  // on every page when none of it is on screen.
  const visibleRefColumnsRef = useRef<string[]>([]);

  // Edit mode state — when active, enables inline cell editing
  const [editMode, setEditMode] = useState(false);
  const [gridReady, setGridReady] = useState(false);
  const activeFilters = useActiveColumnFilters();
  const resettingColumnsRef = useRef(false);
  // REG-828: with a layoutKey the user's columns (order, visibility, widths,
  // pinning) are saved per user on the server; otherwise only widths are kept,
  // in this browser.
  const {
    loaded: layoutLoaded,
    initialColumnState: savedLayout,
    save: saveLayout,
    clear: clearLayout,
    initialDefaults: savedAgainst,
  } = useGridLayout(layoutKey);
  const restoredLayoutKeyRef = useRef<string | null>(null);
  const applyingLayoutRef = useRef(false);
  const { schedule: scheduleRecordPrefetch, cancel: cancelRecordPrefetch } = useHoverPrefetch();

  // Table search state
  const [tableSearch, setTableSearch] = useState('');
  const tableSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchFilterRef = useRef<CRMFilterCondition | null>(null);
  const searchConditionsRef = useRef<QueryConditions | null>(null);

  useEffect(() => {
    if (refreshKey == null) return;
    gridApiRef.current?.api.refreshServerSide({ purge: true });
  }, [refreshKey]);

  const handleTableSearchChange = useCallback((value: string) => {
    setTableSearch(value);
    if (tableSearchTimeoutRef.current) clearTimeout(tableSearchTimeoutRef.current);

    const api = gridApiRef.current?.api;
    if (!api) return;

    // Clear immediately when input is empty
    if (!value.trim()) {
      searchFilterRef.current = null;
      searchConditionsRef.current = null;
      api.refreshServerSide({ purge: true });
      return;
    }

    // Debounce non-empty searches using the grid's configured column/operator.
    tableSearchTimeoutRef.current = setTimeout(() => {
      if (searchMode === 'plain_fts') {
        searchFilterRef.current = null;
        searchConditionsRef.current = buildTableSearchConditions({
          searchTerm: value.trim(),
          fields: Object.values(fields),
        });
      } else {
        searchConditionsRef.current = null;
        searchFilterRef.current = {
          filterType: 'text',
          type: searchMode,
          filter: value.trim(),
        };
      }
      api.refreshServerSide({ purge: true });
    }, 300);
  }, [fields, searchMode]);

  // Warning state
  const [warningState, setWarningState] = useState<WarningState>({
    isOpen: false,
    message: '',
    title: '',
    onConfirm: () => {},
    onCancel: () => {}
  });

  const onWarning = useCallback((title: string, message: string, onConfirm: () => void, onCancel: () => void) => {
    setWarningState({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setWarningState(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => {
        onCancel();
        setWarningState(prev => ({ ...prev, isOpen: false }));
      }
    });
  }, []);

  // Helper functions
  const isClientSideRow = useCallback((rowData: DynamicRecord): boolean => {
    return String(rowData.id).startsWith('temp_');
  }, []);

  const removeClientSideRow = useCallback((rowId: string | number): void => {
    clientSideRowsRef.current = clientSideRowsRef.current.filter((row) => row.id !== rowId);
    if (gridApiRef.current) {
      gridApiRef.current.api.applyServerSideTransaction({
        remove: [{ id: rowId }],
      });
    }
  }, []);

  // Grid context
  const gridContext: GridContext = useMemo(() => ({
    tableName,
    schema: schemaName,
    removeClientSideRow,
    isClientSideRow,
  }), [tableName, schemaName, removeClientSideRow, isClientSideRow]);

  // Row selection configuration
  const rowSelection: RowSelectionConfig | false = useMemo(() => {
    if (gridConfig.ui_data.rowSelection === 'singleRow') {
      return { mode: 'singleRow' };
    } else if (gridConfig.ui_data.rowSelection === 'multipleRows') {
      return { mode: 'multipleRows' };
    } else {
      return false;
    }
  }, [gridConfig.ui_data.rowSelection]);

  // Formatters
  const formatCurrency: CurrencyFormatter = useCallback((value: number | null, scale: number = 0): string => {
    if (value === null || value === undefined || !isFinite(value)) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: scale,
      maximumFractionDigits: scale,
    }).format(value);
  }, []);

  const formatNumber: NumberFormatter = useCallback((value: number | null, scale: number = 0): string => {
    if (value === null || value === undefined || !isFinite(value)) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: scale,
      maximumFractionDigits: scale,
    }).format(value);
  }, []);

  // Event handlers
  const handleRowClicked = useCallback((event: any) => {
    // In edit mode, let AG Grid handle the click for inline editing
    if (editMode) return;
    if (onRecordOpen && event?.data?.id) {
      // Don't open sidebar for unsaved client-side rows
      if (!isClientSideRow(event.data)) {
        onRecordOpen(event.data.id);
      }
    }
  }, [onRecordOpen, isClientSideRow, editMode]);

  const handleCellMouseOver = useCallback((event: any) => {
    // Selection and edit grids do not have ordinary row-open semantics, so a
    // hover there must not speculate on a destination the user cannot open.
    if (editMode || onSelectionChanged || !onRecordOpen || !event?.data?.id || isClientSideRow(event.data)) return;
    scheduleRecordPrefetch(() => prefetchRecordDestinationIfAbsent({
      tableName,
      id: event.data.id,
    }, metadata));
  }, [editMode, isClientSideRow, metadata, onRecordOpen, onSelectionChanged, scheduleRecordPrefetch, tableName]);

  const onCellEditingStarted = useCallback((_event: CRMCellEditingStartedEvent) => {
    return;
  }, []);

  const handleNewRow = useCallback(() => {
    const uiFields = gridConfig?.ui_data?.fields;
    const filters = topLevelFilters;
    
    // Initialize with standard audit fields
    let defaultNewRowData: DynamicRecord = {
      id: 0,
      created_at: '',
      updated_at: '',
      created_by: 0, 
      updated_by: 0  
    } as DynamicRecord;

    // Apply metadata-defined default values
    if (metadata?.fieldData) {
      const defaults = applyDefaultValues(tableName, metadata.fieldData);
      defaultNewRowData = { ...defaultNewRowData, ...defaults };
    }

    // Ensure all UI fields are at least null if not set by defaults
    uiFields?.forEach((field) => {
      if (field.field.includes('.')) {
        const [parentField, childField] = field.field.split('.');
        if (!defaultNewRowData[parentField]) {
          defaultNewRowData[parentField] = {};
        }
        if (defaultNewRowData[parentField][childField] === undefined) {
          defaultNewRowData[parentField][childField] = null;
        }
      } else {
        if (defaultNewRowData[field.field] === undefined) {
          defaultNewRowData[field.field] = null;
        }
      }
    });

    // Apply top level filters as default values (they override metadata defaults)
    Object.entries(filters).forEach(([key, value]) => {
      if (value.type === 'equals') {
        defaultNewRowData[key] = value.filter;
      }
    });

    const newTempId = `temp_${clientSideRowsRef.current.length + 1}`;
    defaultNewRowData.id = newTempId as any;
    clientSideRowsRef.current = [defaultNewRowData, ...clientSideRowsRef.current];

    // Add to grid
    if (gridApiRef.current) {
      gridApiRef.current.api.applyServerSideTransaction({
        add: [defaultNewRowData],
        addIndex: 0,
      });
    }

    // Invalidate cache for future navigation
    invalidateAllQueriesForTableNoRefetch(tableName);
  }, [gridConfig?.ui_data?.fields, tableName, metadata?.fieldData]);

  // Cell value changed handler parameters
  const cellValueChangedParams: CellValueChangedParams = useMemo(() => ({
    tableName,
    schemaName,
    gridConfig,
    fieldData: metadata?.fieldData,
    isClientSideRow,
    clientSideRowsRef,
    gridApiRef,
    onWarning
  }), [
    tableName,
    schemaName,
    gridConfig,
    metadata?.fieldData,
    isClientSideRow,
    onWarning
  ]);

  // Cell value changed handler
  const onCellValueChanged = useMemo(() => 
    createCellValueChangedHandler(cellValueChangedParams),
    [cellValueChangedParams]
  );

  // Column configuration parameters
  const columnConfigParams: ColumnConfigParams = useMemo(() => ({
    // Disable editing when readOnly (for deleted parent records)
    isEditable: readOnly ? false : (gridConfig.ui_data.isEditable !== false),
    // REG-739: only the fields this user's role may write (merchant notes and
    // contacts for a rep, everything with edit_deals).
    canEditField: (fieldName: string) => canEditField(tableName, fieldName),
    // Same condition as the grid's singleClickEdit below.
    clickToEdit: editMode || !onRecordOpen,
    showFilters: gridConfig.ui_data.showFilters !== false,
    showMenu: gridConfig.ui_data.showMenu !== false,
    clientSideTables,
    metadata: columnMetadata,
    schemaName,
    tableName,
    source,
    tablePicklists,
    formatNumber,
    formatCurrency,
    isClientSideRow,
    onCellValueChanged
  }), [
    readOnly,
    editMode,
    onRecordOpen,
    canEditField,
    gridConfig.ui_data.isEditable,
    gridConfig.ui_data.showFilters,
    gridConfig.ui_data.showMenu,
    clientSideTables,
    columnMetadata,
    schemaName,
    tableName,
    source,
    tablePicklists,
    formatNumber,
    formatCurrency,
    isClientSideRow,
    onCellValueChanged
  ]);

  // Column configuration
  const columnsConfig: ExtendedColDef[] = useMemo(() => {
    if (!fields || !Object.keys(fields).length) {
      return [];
    }

    // Disable add/delete when readOnly (for deleted parent records)
    const showAdd = readOnly ? false : (gridConfig?.ui_data?.showAdd || false);
    const showDelete = readOnly ? false : (gridConfig?.ui_data?.showDelete || false);
    const showViewRecord = gridConfig?.ui_data?.showViewRecord || false;

    const configs: ExtendedColDef[] = [];

    // Only add Actions column when not hidden
    if (!hideActions) {
      // Use custom action components if provided, otherwise use defaults
      const ActionCellRendererComponent = customActionCellRenderer || ActionCellRenderer;
      const ActionHeaderComponent = customActionHeader || ActionHeader;

      // Merge custom params with default params
      const cellRendererParams = customActionCellRendererParams
        ? { ...customActionCellRendererParams, showDelete, showViewRecord, routeName }
        : { showDelete, showViewRecord, routeName };
      const headerComponentParams = {
        gridConfig,
        handleNewRow: () => handleNewRow(),
        showAdd,
        routeName,
        ...customActionHeaderParams
      };

      configs.push({
        headerName: 'Actions',
        field: 'actions',
        cellRenderer: ActionCellRendererComponent,
        headerComponent: ActionHeaderComponent as any,
        cellRendererParams,
        headerComponentParams,
        filter: false,
        sortable: false,
        editable: false,
        pinned: 'left',
        suppressColumnsToolPanel: true,
        lockPosition: true,
        menuTabs: []
      });
    }

    // Add configurations for all fields in UI config
    Object.values(gridConfig?.ui_data?.fields || []).forEach(field => {
      if (field?.dbCol) {
        configs.push(getColumnConfigFromField(field.dbCol, field, columnConfigParams));
      }
    });

    // Add hidden columns for fields not in the gridConfig
    Object.values(fields).forEach((field: any) => {
      if (field && !gridConfig?.ui_data?.fields?.find((f: any) => f.dbCol?.name === field.name)) {
        const config = getColumnConfigFromField(field, undefined, columnConfigParams);
        config.hide = true;
        // A list layout can rename a column it keeps hidden (ui_data.column_labels),
        // e.g. the stored merchant status next to the derived Status (REG-832).
        const hiddenLabel = (gridConfig?.ui_data as { column_labels?: Record<string, string> } | undefined)
          ?.column_labels?.[field.name];
        if (hiddenLabel) config.headerName = hiddenLabel;
        configs.push(config);
      }
    });

    // Presentation-only columns supplied by the page (REG-676). Forced
    // non-editable/unsortable/unfilterable because they have no server field.
    (extraColumnDefs ?? []).forEach((extra) => {
      configs.push({ ...extra, editable: false, sortable: false, filter: false, suppressHeaderMenuButton: true });
    });

    return configs;
  }, [
    fields,
    gridConfig,
    customActionCellRenderer,
    customActionHeader,
    customActionCellRendererParams,
    customActionHeaderParams,
    columnConfigParams,
    handleNewRow,
    routeName,
    extraColumnDefs,
  ]);
  // REG-828: this table's default layout is its Page Builder list layout
  // (table_ui_component). A user's saved layout records which version of it it
  // was saved against, so columns an admin shows later are not silently missing.
  const currentDefaults = useMemo<GridDefaultsSnapshot>(() => ({
    version: Date.parse(String((gridConfig as { updated_at?: string | null })?.updated_at ?? '')) || 0,
    visible: visibleColumnIds(columnsConfig.filter((column) => column.field !== 'actions')),
  }), [columnsConfig, gridConfig]);

  // REG-739: no Edit toggle when the user's role may change none of the fields.
  const hasEditableColumn = useMemo(
    () => columnsConfig.some((column) => column.context?.editable === true),
    [columnsConfig],
  );

  // Data source parameters
  const dataSourceParams: DataSourceParams = useMemo(() => ({
    tableName,
    schemaName,
    source,
    topLevelFilters,
    searchFilterRef,
    searchConditionsRef,
    searchColumn,
    searchColumns,
    controlledData,
    metadata: datasourceMetadata,
    clientSideRowsRef,
    serverSideCacheRef,
    visibleRefColumnsRef
  }), [
    tableName,
    schemaName,
    source,
    topLevelFilters,
    searchColumn,
    searchColumns,
    controlledData,
    datasourceMetadata
  ]);

  // REG-664: seed the expanded-relationship columns from the column config
  // during render, so the very first fetch already selects whatever the grid is
  // about to draw. Column-chooser changes are picked up by handleColumnVisible.
  visibleRefColumnsRef.current = useMemo(() => (
    columnsConfig
      .filter(config => !config.hide && typeof config.field === 'string' && config.field.endsWith('_ref'))
      .map(config => config.field as string)
      .sort()
  ), [columnsConfig]);

  // Showing a previously hidden expanded column needs a refetch: it was left out
  // of the select, so the rows in the store have no value for it.
  const handleColumnVisible = useCallback(() => {
    const api = gridApiRef.current?.api;
    if (!api) return;

    const next = api
      .getAllDisplayedColumns()
      .map((column: any) => column.getColId())
      .filter((colId: string) => colId.endsWith('_ref'))
      .sort();

    const previous = visibleRefColumnsRef.current;
    const unchanged = next.length === previous.length
      && next.every((colId: string, index: number) => colId === previous[index]);
    if (unchanged) return;

    visibleRefColumnsRef.current = next;
    api.refreshServerSide({ purge: true });
  }, []);

  // Data source
  const datasource = useMemo(() => createServerSideDataSource(dataSourceParams), [
    dataSourceParams
  ]);

  // REG-802: the column menu ends with that column's total, average or most
  // common value over every row the grid's filters and search match.
  const getMainMenuItems = useCallback((params: GetMainMenuItemsParams) => {
    if (controlledData) return params.defaultItems;
    const colId = params.column.getColId();
    const plan = tableColumnStatPlan(fields[colId], Boolean(tablePicklists[colId]));
    if (!plan) return params.defaultItems;
    const conditions = buildGridQueryConditions({
      filterModel: params.api.getFilterModel() as CRMFilterModel,
      topLevelFilters,
      searchFilter: searchFilterRef.current,
      searchConditions: searchConditionsRef.current,
      searchColumn,
      searchColumns,
      metadata: datasourceMetadata,
      tableName,
    });
    return columnMenuWithStat(params, {
      queryKey: ['table-column-stat', schemaName, tableName, source ?? 'view', conditions, colId],
      load: () => fetchTableColumnStat({ tableName, schemaName, source, conditions, plan }),
    });
  }, [controlledData, datasourceMetadata, fields, schemaName, searchColumn, searchColumns, source, tableName, tablePicklists, topLevelFilters]);

  // Apply other users' inserts/updates/deletes to the rendered grid
  useLiveGridUpdates({
    tableName,
    schemaName,
    source,
    gridApiRef,
    topLevelFilters,
    searchFilterRef,
    searchConditionsRef,
    searchColumn,
    searchColumns,
    metadata: datasourceMetadata,
    clientSideRowsRef,
    // Controlled grids are fed their rows by the parent component
    enabled: !controlledData
  });

  // Controlled mode callbacks
  const handleFilterChanged = useCallback((event: any) => {
    if (controlledMode && onFilterChanged) {
      const filterModel = event.api.getFilterModel();
      onFilterChanged(filterModel);
    }
  }, [controlledMode, onFilterChanged]);

  const handleSortChanged = useCallback((event: any) => {
    if (controlledMode && onSortChanged) {
      const columnState = event.api.getColumnState();
      const sortModel = columnState
        .filter((col: any) => col.sort !== null && col.sort !== undefined)
        .sort((a: any, b: any) => (a.sortIndex || 0) - (b.sortIndex || 0))
        .map((col: any) => ({
          colId: col.colId,
          sort: col.sort,
          sortIndex: col.sortIndex
        }));
      onSortChanged(sortModel);
    }
  }, [controlledMode, onSortChanged]);

  /**
   * Page number in the URL (REG-592/REG-610): leaving a table and pressing Back
   * has to come back to the page you were on. The entry is rewritten in place
   * rather than pushed, so paging through a grid does not bury the previous
   * route under a stack of history entries — but the page still travels with
   * the entry, which is what Back restores.
   */
  const urlPage = Math.max(1, Number(new URLSearchParams(location.search).get(GRID_PAGE_PARAM)) || 1);
  // Until the grid has been moved to the page named in the URL, its own
  // pagination events describe page 1 and must not overwrite it.
  const pageRestoredRef = useRef(false);

  useEffect(() => {
    pageRestoredRef.current = false;
  }, [routeName]);

  const writePageToUrl = useCallback((page: number) => {
    const params = new URLSearchParams(window.location.search);
    const current = Math.max(1, Number(params.get(GRID_PAGE_PARAM)) || 1);
    if (current === page) return;
    if (page <= 1) params.delete(GRID_PAGE_PARAM);
    else params.set(GRID_PAGE_PARAM, String(page));
    const search = params.toString();
    navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  }, [navigate]);

  const restorePageFromUrl = useCallback((api: any) => {
    if (!syncPageToUrl || pageRestoredRef.current) return;
    pageRestoredRef.current = true;
    if (urlPage <= 1) return;
    try {
      // Pages beyond the row count are ignored by AG Grid rather than erroring.
      api.paginationGoToPage(urlPage - 1);
    } catch (error) {
      console.warn('Could not restore the grid page from the URL:', error);
    }
  }, [syncPageToUrl, urlPage]);

  /**
   * InternalAGGridReact reshapes AG Grid's event into `{ currentPage, pageSize }`
   * before calling this — there is no `event.api` here. `currentPage` is 0-based.
   */
  const handlePaginationChanged = useCallback(({ currentPage, pageSize }: { currentPage: number; pageSize: number }) => {
    if (syncPageToUrl && pageRestoredRef.current) {
      writePageToUrl(currentPage + 1);
    }
    if (controlledMode && onPageSizeChanged) {
      onPageSizeChanged(pageSize || 50);
    }
  }, [controlledMode, onPageSizeChanged, syncPageToUrl, writePageToUrl]);

  const colWidthStorageKey = layoutKey ? undefined : `grid_col_widths_${gridConfig.name}`;

  /** Widths the user saved: from the server layout, or this browser's widths. */
  const readSavedWidths = useCallback((): Record<string, number> => {
    if (layoutKey) {
      return Object.fromEntries((savedLayout ?? [])
        .filter((col) => typeof col.width === 'number')
        .map((col) => [col.colId, col.width as number]));
    }
    try {
      return JSON.parse(localStorage.getItem(colWidthStorageKey!) || '{}');
    } catch {
      return {};
    }
  }, [colWidthStorageKey, layoutKey, savedLayout]);

  /** Save the column layout the user changed (never the sort: the URL and datasource own it). */
  const persistLayout = useCallback((event: { source?: string }) => {
    const api = gridApiRef.current?.api;
    if (!layoutKey || !api || applyingLayoutRef.current || resettingColumnsRef.current || !isUserColumnEvent(event)) return;
    if (restoredLayoutKeyRef.current !== layoutKey) return;
    saveLayout(api.getColumnState().map(({ sort: _sort, sortIndex: _sortIndex, ...column }: any) => column), currentDefaults);
  }, [currentDefaults, layoutKey, saveLayout]);

  // Columns whose size is pinned in metadata (ui_data.fields[].width or .flex)
  // keep that size and are excluded from first-render auto-sizing.
  const explicitWidthCols = useMemo(() => new Set(
    (gridConfig?.ui_data?.fields || [])
      .filter((f: any) => f?.width != null || f?.flex != null)
      .map((f: any) => f?.dbCol?.name || f?.field)
      .filter(Boolean)
  ), [gridConfig?.ui_data?.fields]);

  const handleFirstDataRendered = useCallback((params: any) => {
    if (!initialDataTelemetryRef.current) {
      initialDataTelemetryRef.current = true;
      workflowBreadcrumb('record.list.ready', {
        table: tableName,
        surface: routeName ? 'route' : 'embedded',
      });
    }
    // Saved widths: columns with a saved width skip auto-sizing entirely
    const savedWidths = readSavedWidths();

    // Auto-size only columns without a user-saved width or a metadata width
    const colsToAutoSize = params.api.getColumns()
      ?.filter((col: any) => !savedWidths[col.getColId()] && !explicitWidthCols.has(col.getColId()))
      .map((col: any) => col.getColId()) || [];

    if (colsToAutoSize.length > 0) {
      params.api.autoSizeColumns(colsToAutoSize);
    }

    // Apply sizing rule: clamp(content_width, header_width, 300px) for auto-sized columns only
    const MAX_COL_WIDTH = 300;

    const stateOverrides: { colId: string; width: number }[] = [];
    params.api.getColumns()?.forEach((col: any) => {
      if (savedWidths[col.getColId()]) return; // skip — already set in onGridReady
      if (explicitWidthCols.has(col.getColId())) return; // skip — width pinned in metadata
      const minHeaderWidth = headerMinWidth(col.getColDef().headerName || '');
      const currentWidth = col.getActualWidth();
      const targetWidth = Math.min(Math.max(currentWidth, minHeaderWidth), MAX_COL_WIDTH);
      if (currentWidth !== targetWidth) {
        stateOverrides.push({ colId: col.getColId(), width: targetWidth });
      }
    });

    if (stateOverrides.length > 0) {
      params.api.applyColumnState({ state: stateOverrides });
    }

    // Only now do rows exist to page through.
    restorePageFromUrl(params.api);
  }, [explicitWidthCols, readSavedWidths, restorePageFromUrl, routeName, tableName]);

  const handleColumnResized = useCallback((event: any) => {
    // Only save when user finishes dragging (not intermediate resize events)
    if (resettingColumnsRef.current || !event.finished || !event.columns?.length) return;
    if (layoutKey) {
      persistLayout(event);
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(colWidthStorageKey!) || '{}');
      event.columns.forEach((col: any) => {
        saved[col.getColId()] = col.getActualWidth();
      });
      localStorage.setItem(colWidthStorageKey!, JSON.stringify(saved));
    } catch {
      // localStorage unavailable — silently skip
    }
  }, [colWidthStorageKey, layoutKey, persistLayout]);

  const handleColumnMoved = useCallback((event: any) => {
    if (event.finished) persistLayout(event);
  }, [persistLayout]);

  const handleColumnVisibleAndSave = useCallback((event: any) => {
    handleColumnVisible();
    persistLayout(event);
  }, [handleColumnVisible, persistLayout]);

  // Restore the user's saved layout once the grid and the layout are ready.
  useEffect(() => {
    const api = gridApiRef.current?.api;
    if (!layoutKey || !gridReady || !api || !layoutLoaded) return;
    if (restoredLayoutKeyRef.current === layoutKey) return;
    const saved = reconcileSavedLayout(
      filterColumnState(savedLayout, (api.getColumns() ?? []).map((column: any) => column.getColId())),
      savedAgainst,
      currentDefaults,
      currentDefaults.visible,
    ).map(({ sort: _sort, sortIndex: _sortIndex, ...column }: any) => column);
    applyingLayoutRef.current = true;
    if (saved.length > 0) api.applyColumnState({ state: saved, applyOrder: true });
    applyingLayoutRef.current = false;
    restoredLayoutKeyRef.current = layoutKey;
    // A restored layout can show an expanded column the first fetch left out.
    if (saved.length > 0) handleColumnVisible();
  }, [gridReady, handleColumnVisible, layoutKey, layoutLoaded, savedLayout]);

  const onGridReady = useCallback((params: any) => {
    gridApiRef.current = {
      api: params.api,
      context: params.context,
    };
    setGridReady(true);
    activeFilters.attach(params.api);

    // Apply saved column widths immediately so returning users see correct widths
    // before data loads — eliminates the flash from default → saved width. A
    // server layout is applied by its own effect once it has loaded.
    if (colWidthStorageKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(colWidthStorageKey) || '{}');
        if (Object.keys(saved).length > 0) {
          const state = Object.entries(saved).map(([colId, width]) => ({ colId, width: width as number }));
          params.api.applyColumnState({ state });
        }
      } catch { /* localStorage unavailable */ }
    }

    // Apply controlled state if in controlled mode
    if (controlledMode) {
      if (controlledFilterModel) {
        params.api.setFilterModel(controlledFilterModel);
      }
      
      // Apply controlled column state
      if (controlledColumnState) {
        params.api.applyColumnState({ state: controlledColumnState });
      } else if (controlledSortModel && controlledSortModel.length > 0) {
        const currentColumnState = params.api.getColumnState();
        
        // Reset all sorts first
        const resetColumnState = currentColumnState.map((col: any) => ({
          ...col,
          sort: null,
          sortIndex: null
        }));
        
        // Apply sorts from controlledSortModel
        controlledSortModel.forEach((sortItem, index) => {
          const columnIndex = resetColumnState.findIndex((col: any) => col.colId === sortItem.colId);
          if (columnIndex !== -1) {
            resetColumnState[columnIndex].sort = sortItem.sort;
            resetColumnState[columnIndex].sortIndex = sortItem.sortIndex || index;
          }
        });
        
        params.api.applyColumnState({ state: resetColumnState });
      }
      
      if (controlledPageSize) {
        try {
          if (typeof params.api.paginationSetPageSize === 'function') {
            params.api.paginationSetPageSize(controlledPageSize);
          } else if (typeof params.api.setGridOption === 'function') {
            params.api.setGridOption('paginationPageSize', controlledPageSize);
          } else {
            console.warn('Could not set controlled page size: pagination API not available');
          }
        } catch (error) {
          console.error('Error setting controlled page size:', error);
        }
      }
    }
  }, [
    activeFilters.attach,
    tableName,
    gridConfig,
    topLevelFilters,
    controlledData,
    controlledMode,
    controlledFilterModel,
    controlledSortModel,
    controlledColumnState,
    controlledPageSize,
    colWidthStorageKey
  ]);

  const handleResetColumns = useCallback(() => {
    const api = gridApiRef.current?.api;
    if (!api) return;

    resettingColumnsRef.current = true;
    try {
      resetColumnLayout(api, colWidthStorageKey);
      if (layoutKey) clearLayout();
      // Metadata-sized fields keep their configured size; all other fields use
      // the same bounded content sizing as the first render.
      handleFirstDataRendered({ api });
      onColumnStateChanged?.(api.getColumnState());
    } finally {
      resettingColumnsRef.current = false;
    }
  }, [clearLayout, colWidthStorageKey, handleFirstDataRendered, layoutKey, onColumnStateChanged]);

  // REG-828: a manage_default_layouts holder makes the columns on screen this
  // table's default layout, in its Page Builder list layout (save_table_list_layout).
  // Their own saved layout is cleared so they follow the default like everyone else.
  const listComponentId = (gridConfig as { id?: number } | undefined)?.id;
  const handleSaveDefaultLayout = useCallback(async () => {
    const api = gridApiRef.current?.api;
    if (!api || !listComponentId) return;
    await saveTableListLayout(listComponentId, api.getColumnState(), new Set(Object.keys(fields)));
    if (layoutKey) clearLayout();
    await invalidateAndRefreshAllQueriesForTable('table_ui_component');
  }, [clearLayout, fields, layoutKey, listComponentId]);

  const handleResetFilters = useCallback(() => {
    const api = gridApiRef.current?.api;
    if (api) resetGridFilters(api);
  }, []);

  // Loading and error states
  if (metadataError) {
    return <p>{metadataError.message}</p>;
  }
  if (metadataIsLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><span className="sky-spinner" /></div>;
  }

  const getExportConfiguration = (): ExportConfiguration => {
    const api = gridApiRef.current!.api;
    const columns = gridExportColumns(api, (key) => Boolean(fields[key]) && key !== 'full_text_search',
      metadata?.routeData?.tableToRouteName);
    const conditions = buildGridQueryConditions({
      filterModel: api.getFilterModel() as CRMFilterModel,
      topLevelFilters,
      searchFilter: tableSearch.trim() && searchMode !== 'plain_fts' ? { filterType: 'text', type: searchMode, filter: tableSearch.trim() } : null,
      searchConditions: tableSearch.trim() && searchMode === 'plain_fts' ? buildTableSearchConditions({ searchTerm: tableSearch.trim(), fields: Object.values(fields) }) : null,
      searchColumn, searchColumns,
      metadata: datasourceMetadata, tableName,
    });
    const selectedRows = api.getSelectedRows().filter((row) => row.id != null && !isClientSideRow(row));
    // The server grid sorts on its first sorted column (else the default),
    // a relationship by its label, then the newest id first.
    const sorted = sortedColumnStates(api)[0];
    const orderBy = adjustOrderByForRelationships({
      column: sorted?.colId ?? gridReadContract.sortColumn,
      direction: sorted ? (sorted.sort === 'asc' ? 'ascending' : 'descending') : gridReadContract.sortDirection as 'descending',
    }, { metadata: datasourceMetadata, tableName });
    const order = controlledData ? clientGridOrder(api) : serverGridOrder([
      { field: orderBy.column, desc: orderBy.direction === 'descending' },
      ...(orderBy.column === 'id' ? [] : [{ field: 'id', desc: true }]),
    ], loadedGridRows(api));
    return {
      name: metadata?.tableData?.byName?.[tableName]?.ui_label || tableName,
      columns, selectedRows, rowKey: 'id', order,
      filterSummary: describeExportFilters(conditions, { dnd: 'DND', full_text_search: 'Search', ...Object.fromEntries(columns.map((column) => [column.key, column.label])) }),
      load: async ({ after, offset, limit, signal, fields: requestedFields, selectedIds }) => {
        if (controlledData) {
          const rows = controlledData.filter((row) => !isClientSideRow(row) && (!selectedIds || selectedIds.includes(row.id)));
          return { rows: rows.slice(offset, offset + limit) as ExportRow[], total: rows.length };
        }
        const selectedFields = new Set(['id', ...requestedFields]);
        for (const key of requestedFields) {
          if (fields[key]?.relationship && source !== 'base') selectedFields.add(`${key}_rel`);
        }
        const { query } = buildQueryForFieldDependentTable({
          tableName, schema: schemaName, source,
          tableInfo: metadata?.tableData?.byName?.[tableName],
          conditions: { ...conditions,
            ...(selectedIds ? { __export_selection: { filterType: 'number', colId: 'id', type: 'in', values: selectedIds } } : {}),
            ...(after !== null ? { __export_cursor: { filterType: 'number', colId: 'id', type: 'greaterThan', filter: after } } : {}) },
          // Walk primary keys so large exports avoid growing OFFSET scans.
          orderBy: { column: 'id', direction: 'ascending' },
          limitOffset: { offset: 0, limit: limit - 1 }, withCount: after === null,
          selectExpression: [...selectedFields].join(','),
        });
        const result = await query.abortSignal(signal);
        if (result.error) throw new Error('Could not load records for export. Check your filters and try again.');
        return { rows: result.data ?? [], total: result.count ?? null };
      },
    };
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div className="sky-grid-toolbar" ref={toolbarRef}>
        <div className="sky-table-search">
          <svg className="sky-table-search-icon" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="sky-table-search-input"
            type="text"
            placeholder={searchPlaceholder}
            value={tableSearch}
            onChange={(e) => handleTableSearchChange(e.target.value)}
          />
        </div>
        {showEditModeToggle && hasEditableColumn && (
          <EditModeToggle
            active={editMode}
            onClick={() => setEditMode(prev => !prev)}
          />
        )}
        <ResetColumnsButton disabled={!gridReady} onReset={handleResetColumns} />
        {layoutKey && listComponentId ? (
          <SaveDefaultLayoutButton disabled={!gridReady} onSave={handleSaveDefaultLayout} />
        ) : null}
        <ResetFiltersButton disabled={!gridReady} activeCount={activeFilters.filters.length} onReset={handleResetFilters} />
        <GridZoomControl zoom={tableZoom} onChange={setTableZoom} />
        <ExportButton disabled={!gridReady} getConfiguration={getExportConfiguration} />
        {toolbarActions}
        {/* Full-width last item: it wraps onto its own line, which the wrap-height hook already subtracts from the grid. */}
        <ActiveFilterChips filters={activeFilters.filters} onRemove={activeFilters.removeFilter} />
      </div>
      <InternalAGGridReact
        key={gridConfig.name}
        heightLayout={(heightLayout === 'domLayout' ? 'normal' : heightLayout) as 'normal' | 'autoHeight'}
        heightOffset={(heightOffset ?? 0) + toolbarWrapExtraHeight}
        zoom={tableZoom}
        gridContext={gridContext}
        showPagination={showPagination}
        pageSize={controlledPageSize || gridReadContract.pageSize}
        onGridReady={onGridReady}
        tableName={tableName}
        schema={schemaName}
        gridName={gridConfig.name}
        columnsConfig={columnsConfig}
        datasource={datasource}
        onCellEditingStarted={onCellEditingStarted}
        onCellValueChanged={(event: any) => onCellValueChanged(event as CRMCellValueChangedEvent)}
        rowSelection={rowSelection === false ? undefined : (typeof rowSelection === 'object' ? rowSelection.mode === 'singleRow' ? 'single' : 'multiple' : 'single')}
        onRowClicked={onSelectionChanged ? undefined : handleRowClicked}
        onCellMouseOver={onSelectionChanged ? undefined : handleCellMouseOver}
        onCellMouseOut={cancelRecordPrefetch}
        onSelectionChanged={onSelectionChanged}
        singleClickEdit={editMode || !onRecordOpen}
        // Controlled mode callbacks
        onFilterChanged={controlledMode ? handleFilterChanged : undefined}
        onSortChanged={controlledMode ? handleSortChanged : undefined}
        onPaginationChanged={controlledMode || syncPageToUrl ? handlePaginationChanged : undefined}
        onColumnResized={handleColumnResized}
        onColumnVisible={layoutKey ? handleColumnVisibleAndSave : handleColumnVisible}
        onColumnMoved={layoutKey ? handleColumnMoved : undefined}
        onColumnPinned={layoutKey ? persistLayout : undefined}
        onFirstDataRendered={handleFirstDataRendered}
        getMainMenuItems={getMainMenuItems}
      />
      
      <EnhancedDialog
        isOpen={warningState.isOpen}
        onClose={warningState.onCancel}
        title="Warning"
      >
        <div className="sky-dialog-body">
          <div style={{ marginBottom: 10, fontSize: 14, fontWeight: 600 }}>
            {warningState.title}
          </div>
          <div>{warningState.message}</div>
        </div>
        <div className="sky-dialog-footer">
          <button className="sky-btn sky-btn-ghost" onClick={warningState.onCancel}>Cancel</button>
          <button className="sky-btn sky-btn-primary" onClick={warningState.onConfirm}>Yes</button>
        </div>
      </EnhancedDialog>
    </div>
  );
});

TableGrid.displayName = 'TableGrid';

export default TableGrid;
